import { User, UserTypes, UserWithToken } from '$core/models/user';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import { SessionService } from './session.service';
import { Observable } from 'rxjs';

export interface RegistrationDataIndividual {
  phone: string;
  email: string;
  password: string;
  captcha: string;
  key: string;
}

export interface RegistrationDataLegals {
  trn: string;
  email: string;
  password: string;
}

export interface SmsCheckData {
  phone: string;
  code: string;
}

export interface CheckResult {
  status: string;
  code?: string; // Not used on production
}

export interface ResetPasswordRequestData {
  login: string;
  type: UserTypes;
  captcha?: string;
  key?: string;
}

export interface ResetPasswordData {
  code: string;
  password: string;
  password_confirmation: string;
  phone?: string;
}

export interface ChangePasswordData {
  old_password: string;
  password: string;
  password_confirmation: string;
}

export interface UpdatePhoneData {
  phone: string;
  password: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(
    private http: HttpClient,
    private session: SessionService
  ) {}

  /**
   * @description Регистрация для физического лица
   * @param {RegistrationDataIndividual} body
   * @returns
   */
  public registerIndividual(body: RegistrationDataIndividual) {
    return this.http.post<UserWithToken>('/v1/register-individuals', body);
  }

  /**
   * @description Регистрация для юридического лица
   * @param {RegistrationDataLegals} body
   * @returns
   */
  public registerLegals(body: RegistrationDataLegals) {
    return this.http.post<{ status: string }>('/v2/register-legals', body);
  }

  /**
   * @description Проверка данных для регистрации, если пройдена успешно, отсылает SMS
   * @param {RegistrationDataIndividual} body
   * @returns
   */
  public credentialCheck(body: RegistrationDataIndividual) {
    return this.http.post<CheckResult>('/v1/credentials-check', body);
  }

  /**
   * @description Проверка кода из SMS для регистрации
   * @param {SmsCheckData} body
   * @returns
   */
  public checkSms(body: SmsCheckData) {
    return this.http.post<{ status: string }>('/v1/sms-check', body);
  }

  /**
   * @description Запросить сброс пароля
   * @param {ResetPasswordRequestData} body
   * @returns
   */
  public passwordReset(body: ResetPasswordRequestData) {
    return this.http.post<CheckResult>('/v2/password-reset', body);
  }

  /**
   * @description Confirm new email address
   * @param {{ login: string; type: UserTypes }} body
   * @returns
   */
  public confirmEmail(body: { code: string }) {
    return this.http.post<CheckResult>('/v1/email/confirmation', body).pipe(
      tap((res) => {
        if (res.status === 'ok') {
          this.session.getSession();
        }
      })
    );
  }

  /**
   * @description Проверка кода для сброса пароля
   * @param {SmsCheckData} body
   * @returns
   */
  public passwordResetCheck(body: SmsCheckData) {
    return this.http.post<{ status: string }>(
      '/v1/password-reset/check ',
      body
    );
  }

  /**
   * @description Установить новый пароль для пользователя
   * @param {ResetPasswordData} body
   * @returns
   */
  public passwordResetConfirm(body: ResetPasswordData) {
    return this.http.post<UserWithToken>('/v2/password-reset/confirm', body);
  }

  /**
   * @description Запросить письмо подтверждения email повторно
   * @returns
   */
  public confirmationEmailResend(body: { login: string; email?: string }) {
    return this.http.post<{ status: string }>('/v1/email/resend', body);
  }

  /**
   * @description Запросить письмо подтверждения изменения email повторно
   * @returns
   */
  public updateEmailResend() {
    return this.http.post<{ status: string }>('/v1/email-update/resend', {});
  }

  /**
   * @description Запросить изменение телефона пользователя
   * @param {UpdatePhoneData} body
   * @returns
   */
  public updatePhone(body: UpdatePhoneData) {
    return this.http.post<CheckResult>('/v1/users/update-phone', body);
  }

  /**
   * @description Подтвердить изменение телефона пользователя
   * @param {SmsCheckData} body
   * @returns
   */
  public updatePhoneConfirm(body: SmsCheckData) {
    return this.http.post<UserWithToken>(
      '/v1/users/update-phone/confirm',
      body
    );
  }

  /**
   * @description Изменить пароль пользователя
   * @param {SmsCheckData} body
   * @returns
   */
  public changePassword(body: ChangePasswordData) {
    return this.http.put<UserWithToken>('/v1/users/self', body);
  }

  /**
   * @description Изменить email пользователя
   * @param {string} email
   * @returns
   */
  public changeEmail(email: string) {
    return this.http.put<User>('/v1/users/self', { email });
  }

  /**
   * @description Изменить имя пользователя
   * @param {string} name
   * @returns
   */
  public changeName(name: string) {
    return this.http.put<User>('/v1/users/self', { name });
  }

  public updateUser(data: { [key: string]: any }) {
    return this.http.put<User>('/v1/users/self', data).pipe(
      tap((res: User) => {
        this.session.session = res;
      })
    );
  }

  public deleteUser(data: { [key: string]: any }) {
    return this.http.delete('/v1/users', data);
  }
}
